import type { FC } from 'react';

import { useFlags } from '@mint-lib/routing-context';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { SidebarItem } from '@myn/mui';

import { MP_LEVELS } from '../../../consts/index.js';
import { levelIcons } from '../../../consts/sidebar.js';
import { useChosenLevel } from '../../../hooks/useChosenLevel.js';

type Props = {
  nestingLevel?: number;
  isExpanded: Record<string, boolean>;
  items: MediaPlan.SidebarItem[];
  handleItemClick: (item: MediaPlan.SidebarItem) => void;
  toggleSubMenu: (mediaDataId: string, level: string) => void;
};

const SidebarItems: FC<Props> = ({
  nestingLevel = 1,
  items,
  isExpanded,
  handleItemClick,
  toggleSubMenu,
}) => {
  const ff = useFlags('@mint/arm', ['disableActivityLevel']);
  const { id } = useChosenLevel();

  return (
    <>
      {items.map((item) => {
        const expandable = !!item.subItems.length;
        const levelIndent = 8 * nestingLevel;
        const paddingLeft = levelIndent + (expandable ? 8 : 32);
        const isActivityDisabled =
          ff.disableActivityLevel.enabled && item.level === MP_LEVELS.ACTIVITY;

        return (
          <div key={item.mediaDataId}>
            <SidebarItem
              style={{
                paddingLeft: `${paddingLeft}px`,
              }}
              variant="secondary"
              text={item.name}
              icon={levelIcons[item.level as keyof typeof levelIcons]}
              expandable={expandable}
              onClick={() => handleItemClick(item)}
              onChevronClick={() => toggleSubMenu(item.mediaDataId, item.level)}
              open={isExpanded[item.mediaDataId]}
              selected={item.mediaDataId === id}
              tooltipPlacement="right"
              aria-label="Sidebar item"
              disabled={isActivityDisabled}
              sx={
                // temporary solution, SidebarItem will be replaced with tw components as soon as new component is implemented
                isActivityDisabled
                  ? (theme) => ({
                      '&.Mui-disabled': {
                        opacity: 1,
                      },
                      '& > *:not([data-testid="chevron-down-icon"])': {
                        opacity: theme.palette.action.disabledOpacity,
                      },
                      '& [data-testid="chevron-down-icon"]': {
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                      },
                    })
                  : undefined
              }
            />
            {isExpanded[item.mediaDataId] && item.subItems && (
              <SidebarItems
                nestingLevel={nestingLevel + 1}
                items={item.subItems}
                isExpanded={isExpanded}
                handleItemClick={handleItemClick}
                toggleSubMenu={toggleSubMenu}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

export default SidebarItems;
